
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type VolumetricShaderUniforms = {
	uDepthMap: WebGLUniformLocation,
	uInvViewProjection: WebGLUniformLocation,
	uShadowCascadeMap0: WebGLUniformLocation,
	uShadowCascadeMap1: WebGLUniformLocation,
	uShadowCascadeMap2: WebGLUniformLocation,
	uShadowCascadeMap3: WebGLUniformLocation,
	uCascadeViewMatrix: WebGLUniformLocation[],
	uCascadeSplits: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uCameraPosition: WebGLUniformLocation,
	uSunDirection: WebGLUniformLocation,
	uSunColor: WebGLUniformLocation,
	uFogDensity: WebGLUniformLocation,
	uScatteringCoeff: WebGLUniformLocation,
	uStepCount: WebGLUniformLocation,
	uFrameJitter: WebGLUniformLocation
};

export type VolumetricShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type VolumetricShaderSettings = string[];

export class VolumetricShader extends ShaderBase<VolumetricShaderUniforms, VolumetricShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uDepthMap;
uniform mat4 uInvViewProjection;

// CSM shadow cascade data
uniform sampler2D uShadowCascadeMap0;
uniform sampler2D uShadowCascadeMap1;
uniform sampler2D uShadowCascadeMap2;
uniform sampler2D uShadowCascadeMap3;
uniform mat4 uCascadeViewMatrix[4];
uniform vec4 uCascadeSplits;
uniform mat4 uViewMatrix;

// Scene data
uniform vec3 uCameraPosition;
uniform vec3 uSunDirection;
uniform vec3 uSunColor;

// Volumetric parameters
uniform float uFogDensity;
uniform float uScatteringCoeff;
uniform int uStepCount;
uniform float uFrameJitter;

// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---

#define SHADOW_BIAS_VOL 0.003
#define MAX_MARCH_DISTANCE 80.0

float sampleCascadeShadow(sampler2D cascadeMap, vec3 worldPos, mat4 cascadeMatrix) {
	vec4 shadowCoord = cascadeMatrix * vec4(worldPos, 1.0);
	vec3 proj = shadowCoord.xyz / shadowCoord.w;
	proj = proj * 0.5 + 0.5;

	if (proj.x < 0.0 || proj.x > 1.0 || proj.y < 0.0 || proj.y > 1.0) {
		return 1.0;
	}

	float currentDepth = proj.z - SHADOW_BIAS_VOL;
	float depth = texture(cascadeMap, proj.xy).r;
	return depth < currentDepth ? 0.0 : 1.0;
}

float sampleCSM(vec3 worldPos) {
	float viewDepth = (uViewMatrix * vec4(worldPos, 1.0)).z;

	if (viewDepth > -uCascadeSplits.x) {
		return sampleCascadeShadow(uShadowCascadeMap0, worldPos, uCascadeViewMatrix[0]);
	} else if (viewDepth > -uCascadeSplits.y) {
		return sampleCascadeShadow(uShadowCascadeMap1, worldPos, uCascadeViewMatrix[1]);
	} else if (viewDepth > -uCascadeSplits.z) {
		return sampleCascadeShadow(uShadowCascadeMap2, worldPos, uCascadeViewMatrix[2]);
	} else if (viewDepth > -uCascadeSplits.w) {
		return sampleCascadeShadow(uShadowCascadeMap3, worldPos, uCascadeViewMatrix[3]);
	}
	return 1.0;
}

// Simple hash for temporal jittering
float hash(vec2 p) {
	return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main(void) {
	float depth = texture(uDepthMap, vTexCoord).r;

	vec3 rayOrigin = uCameraPosition;

	// Skip sky pixels (no geometry to march toward) — reversed-Z cleared depth = 0.0
	if (depth <= 0.0) {
		fragColor = vec4(0.0);
		return;
	}

	vec3 worldPos = worldPosFromDepth(depth, vTexCoord, uInvViewProjection);
	vec3 rayDir = normalize(worldPos - rayOrigin);
	float marchDistance = min(length(worldPos - rayOrigin), MAX_MARCH_DISTANCE);

	float stepSize = marchDistance / float(uStepCount);

	// Temporal jitter: offset the starting position to reduce banding
	float jitter = hash(gl_FragCoord.xy + uFrameJitter) * stepSize;

	// Henyey-Greenstein phase function (controls how much light scatters toward camera)
	// Lower g = more uniform scattering, higher g = more glow when looking toward sun
	float cosAngle = dot(rayDir, -normalize(uSunDirection));
	float g = 0.4;
	float phase = (1.0 - g * g) / (4.0 * 3.14159 * pow(1.0 + g * g - 2.0 * g * cosAngle, 1.5));

	vec3 accumLight = vec3(0.0);
	float transmittance = 1.0;

	for (int i = 0; i < 32; i++) {
		if (i >= uStepCount) break;

		float t = jitter + float(i) * stepSize;
		if (t > marchDistance) break;

		vec3 samplePos = rayOrigin + rayDir * t;

		// Sample CSM to check if this point is in sunlight
		float sunVisible = sampleCSM(samplePos);

		// Height-based density: dense near ground, thin at height. heightFalloff
		// is per-metre exponential — 0.04 gives ~halved density every ~17m of
		// altitude, ~10% at 60m. Tuned for terrain-scale outdoor scenes; for
		// indoor / small-scale work the height term has effectively no impact.
		float heightFalloff = 0.04;
		float density = uFogDensity * exp(-heightFalloff * max(samplePos.y, 0.0));
		accumLight += sunVisible * density * transmittance * uSunColor * phase * stepSize;

		// Update transmittance (Beer's law)
		transmittance *= exp(-uScatteringCoeff * density * stepSize);

		// Early exit if fully opaque
		if (transmittance < 0.01) break;
	}

	// Soft clamp to prevent blowout when looking toward the sun
	accumLight = accumLight / (1.0 + accumLight);
	fragColor = vec4(accumLight, 1.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: VolumetricShaderUniformsContainer;
    public attributesContainer: VolumetricShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: VolumetricShaderSettings) {
        super(ctx, VolumetricShader.fragmentShaderSource, VolumetricShader.vertexShaderSource, settings);
        this.uniformsContainer = new VolumetricShaderUniformsContainer(ctx, this);
        this.attributesContainer = new VolumetricShaderShaderAttributesContainer(ctx, this);
    }
}

class VolumetricShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: VolumetricShader) {
        super(ctx);
    }

    public setDepthMap(texture: Texture) : VolumetricShaderUniformsContainer {
        this.setTextureValue('uDepthMap', this.shader.uniforms.uDepthMap, texture, 'sampler2D');
        return this;
    }

    public setInvViewProjection(value: mat4 | number[]) : VolumetricShaderUniformsContainer {
        this.setValue('uInvViewProjection', this.shader.uniforms.uInvViewProjection, value, 'mat4');
        return this;
    }

    public setShadowCascadeMap0(texture: Texture) : VolumetricShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap0', this.shader.uniforms.uShadowCascadeMap0, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap1(texture: Texture) : VolumetricShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap1', this.shader.uniforms.uShadowCascadeMap1, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap2(texture: Texture) : VolumetricShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap2', this.shader.uniforms.uShadowCascadeMap2, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap3(texture: Texture) : VolumetricShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap3', this.shader.uniforms.uShadowCascadeMap3, texture, 'sampler2D');
        return this;
    }

    public setCascadeViewMatrix(index: number, value: mat4 | number[]) : VolumetricShaderUniformsContainer {
        if(index < 0 || index >= 4) throw new Error('Invalid index used for uCascadeViewMatrix: ' + index);
        this.setValue('uCascadeViewMatrix', this.shader.uniforms.uCascadeViewMatrix[index], value, 'mat4');
        return this;
    }

    public setCascadeSplits(value: vec4 | number[]) : VolumetricShaderUniformsContainer {
        this.setValue('uCascadeSplits', this.shader.uniforms.uCascadeSplits, value, 'vec4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : VolumetricShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setCameraPosition(value: vec3 | number[]) : VolumetricShaderUniformsContainer {
        this.setValue('uCameraPosition', this.shader.uniforms.uCameraPosition, value, 'vec3');
        return this;
    }

    public setSunDirection(value: vec3 | number[]) : VolumetricShaderUniformsContainer {
        this.setValue('uSunDirection', this.shader.uniforms.uSunDirection, value, 'vec3');
        return this;
    }

    public setSunColor(value: vec3 | number[]) : VolumetricShaderUniformsContainer {
        this.setValue('uSunColor', this.shader.uniforms.uSunColor, value, 'vec3');
        return this;
    }

    public setFogDensity(value: number) : VolumetricShaderUniformsContainer {
        this.setValue('uFogDensity', this.shader.uniforms.uFogDensity, value, 'float');
        return this;
    }

    public setScatteringCoeff(value: number) : VolumetricShaderUniformsContainer {
        this.setValue('uScatteringCoeff', this.shader.uniforms.uScatteringCoeff, value, 'float');
        return this;
    }

    public setStepCount(value: number) : VolumetricShaderUniformsContainer {
        this.setValue('uStepCount', this.shader.uniforms.uStepCount, value, 'int');
        return this;
    }

    public setFrameJitter(value: number) : VolumetricShaderUniformsContainer {
        this.setValue('uFrameJitter', this.shader.uniforms.uFrameJitter, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uDepthMap', uniform: this.shader.uniforms.uDepthMap, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap0', uniform: this.shader.uniforms.uShadowCascadeMap0, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap1', uniform: this.shader.uniforms.uShadowCascadeMap1, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap2', uniform: this.shader.uniforms.uShadowCascadeMap2, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap3', uniform: this.shader.uniforms.uShadowCascadeMap3, dataType: 'sampler2D' }
        ];
    }
}

class VolumetricShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: VolumetricShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : VolumetricShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : VolumetricShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}